import {
  getDevEnvironmentColour,
  getDevEnvironmentLabel,
} from "./devEnvironment";

type FaviconOptions = {
  iconUrl?: string;
  font?: string;
  textColor?: string;
  shadowColor?: string;
  textOffsetY?: number;
  tintColor?: string; // Optional brand color tint
  badgeText?: string;
  badgeColor?: string;
};

let faviconRequest = 0;

export function setFaviconWithBaseIcon(
  initials: string,
  {
    iconUrl = "/assets/base-icon.png",
    font = "bold 28px sans-serif",
    textColor = "#fff",
    shadowColor = "rgba(0, 0, 0, 0.4)",
    textOffsetY = 2,
    tintColor,
    badgeText,
    badgeColor,
  }: FaviconOptions = {}
): void {
  const request = ++faviconRequest;
  const environmentLabel = getDevEnvironmentLabel();
  const resolvedBadgeText = badgeText ?? environmentLabel ?? undefined;
  const resolvedBadgeColor =
    badgeColor ??
    (environmentLabel
      ? getDevEnvironmentColour(environmentLabel)
      : "#137cbd");
  const img = new Image();
  img.crossOrigin = "anonymous";

  img.onload = () => {
    const dpr = window.devicePixelRatio || 1;
    const size = 128;

    const canvas = document.createElement("canvas");

    canvas.width = canvas.height = size * dpr;
    canvas.style.width = canvas.style.height = `${size}px`;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.warn("Canvas rendering context not available.");
      return;
    }
    ctx.scale(dpr, dpr); // normalize drawing coords to 128×128

    const iconInset = 10; // adjust as needed

    if (tintColor) {
      const off = document.createElement("canvas");
      off.width = off.height = size;
      const offCtx = off.getContext("2d")!;
      offCtx.drawImage(
        img,
        iconInset,
        iconInset,
        size - iconInset * 2,
        size - iconInset * 2
      );
      offCtx.fillStyle = tintColor;
      offCtx.globalCompositeOperation = "source-atop";
      offCtx.fillRect(0, 0, size, size);
      offCtx.globalCompositeOperation = "source-over";

      // Draw white background on main canvas
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
      ctx.fillStyle = "white";
      ctx.fill();

      // Draw tinted image on top
      ctx.drawImage(off, 0, 0);
    } else {
      // No tint: draw white circle + image directly
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
      ctx.fillStyle = "white";
      ctx.fill();

      ctx.drawImage(
        img,
        iconInset,
        iconInset,
        size - iconInset * 2,
        size - iconInset * 2
      );
    }

    // Optional shadow
    ctx.shadowColor = shadowColor;
    ctx.shadowBlur = 4;

    // Draw initials
    ctx.fillStyle = textColor;
    ctx.font = font;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(initials, size / 2, size / 2 + textOffsetY);

    if (resolvedBadgeText) {
      const abbreviatedBadgeText = resolvedBadgeText.slice(0, 2);
      const badgeX = 91;
      const badgeY = 91;
      const badgeRadius = 39;

      ctx.shadowColor = "transparent";
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeRadius, 0, Math.PI * 2);
      ctx.fillStyle = resolvedBadgeColor;
      ctx.fill();
      ctx.lineWidth = 6;
      ctx.strokeStyle = "white";
      ctx.stroke();

      ctx.fillStyle = "white";
      ctx.font = `bold ${abbreviatedBadgeText.length > 1 ? 36 : 52}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(abbreviatedBadgeText, badgeX, badgeY + 1);
    }

    if (request !== faviconRequest) return;

    // Generate data URL and update favicon
    const faviconUrl = canvas.toDataURL("image/png");

    // Remove existing favicons
    const existingLinks =
      document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']");
    existingLinks.forEach((link) => link.remove());

    // Create and append new favicon link
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/png";
    link.href = faviconUrl;
    document.head.appendChild(link);
  };

  img.src = iconUrl;
}
